<template>
  <div class="pb-10">
    <!-- En-tête -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="handleCancel">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Nouvelle facture fournisseur</h1>
          <p class="text-xs text-gray-400">{{ supplier?.name || 'Chargement...' }}</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="handleCancel">
          <i class="fas fa-times text-[11px]"></i>
          Annuler
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="isSubmitting || !canSubmit" @click="handleSubmit">
          <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-[11px]"></i>
          <i v-else class="fas fa-check text-[11px]"></i>
          Générer la facture
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement des commandes...</p>
    </div>

    <!-- Contenu principal -->
    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Informations facture -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Informations de la facture</h3>
            <p class="text-xs text-gray-400 mt-0.5">Définissez les dates et notes</p>
          </div>
          <div class="p-5">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">
                  Date de facture <span class="text-red-500">*</span>
                </p>
                <input type="date" v-model="form.invoice_date"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
                <span v-if="errors.invoice_date" class="flex items-center gap-1.5 text-xs font-medium text-red-500 mt-1">
                  <i class="fas fa-circle-info text-[10px]"></i>
                  {{ errors.invoice_date }}
                </span>
              </div>
              <div>
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Date d'échéance</p>
                <input type="date" v-model="form.due_date" :min="form.invoice_date"
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors" />
              </div>
              <div class="col-span-2">
                <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-1">Notes</p>
                <textarea v-model="form.notes" rows="2" placeholder="Notes additionnelles pour cette facture..."
                  class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Commandes à facturer -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Commandes à facturer</h3>
            <p class="text-xs text-gray-400 mt-0.5">Sélectionnez les commandes à inclure dans la facture</p>
          </div>

          <div v-if="availableOrders.length === 0" class="py-12 text-center">
            <div class="text-gray-300 mb-3">
              <i class="fas fa-circle-info text-3xl"></i>
            </div>
            <p class="text-sm font-medium text-gray-500">Aucune commande disponible</p>
            <p class="text-xs text-gray-400 mt-0.5">Ce fournisseur n'a pas de commandes actives à facturer</p>
          </div>

          <div v-else class="p-5 space-y-3">
            <div v-for="order in availableOrders" :key="order.id"
              class="flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all"
              :class="isOrderSelected(order.id)
                ? 'border-primary/30 bg-primary/5'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/60'"
              @click="toggleOrder(order)">
              <div class="flex-shrink-0">
                <input type="checkbox" :id="`order-${order.id}`" :value="order.id" v-model="selectedOrderIds"
                  class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary/20 cursor-pointer"
                  @change.stop="toggleOrder(order)" @click.stop />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="text-sm font-semibold text-gray-900">{{ order.reference }}</span>
                  <i v-if="isOrderSelected(order.id)" class="fas fa-check text-primary text-[10px]"></i>
                </div>
                <p class="text-xs text-gray-400 mt-0.5">
                  <i class="fas fa-calendar mr-1 text-[10px]"></i>
                  {{ formatDate(order.order_date) }}
                </p>
              </div>
              <div class="text-right flex-shrink-0">
                <p class="text-[11px] text-gray-400">Montant</p>
                <p class="text-sm font-bold text-gray-900">{{ formatCurrency(calculateOrderTotal(order)) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Résumé -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden lg:sticky lg:top-5">
          <div class="px-5 py-3 bg-primary/5 border-b border-primary/10">
            <h3 class="text-sm font-semibold text-primary text-center">Résumé de la facture</h3>
          </div>
          <div class="p-5 space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Fournisseur</span>
              <span class="text-sm font-medium text-gray-900">{{ supplier?.name || '-' }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Commandes sélectionnées</span>
              <span class="text-sm font-semibold text-gray-900">{{ selectedOrderIds.length }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Sous-total</span>
              <span class="text-sm font-semibold text-gray-900">{{ formatCurrency(totalAmount) }}</span>
            </div>

            <div v-if="totalDiscount > 0" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Remise totale</span>
              <span class="text-sm font-medium text-red-500">-{{ formatCurrency(totalDiscount) }}</span>
            </div>

            <div v-if="totalTax > 0" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Taxe totale</span>
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(totalTax) }}</span>
            </div>

            <div class="h-px bg-gray-100"></div>

            <div class="flex justify-between items-center pt-1">
              <span class="text-xs font-medium text-gray-500">Montant total</span>
              <span class="text-lg font-bold text-primary">{{ formatCurrency(grandTotal) }}</span>
            </div>
          </div>

          <div class="px-5 py-4 border-t border-gray-100">
            <button
              class="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSubmitting || !canSubmit" @click="handleSubmit">
              <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-xs"></i>
              <i v-else class="fas fa-check text-xs"></i>
              Générer la facture
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import purchaseOrderService from '@/services/purchaseOrderService'
import supplierService from '@/services/supplierService'
import supplierInvoiceService from '@/services/supplierInvoiceService'
import { useToastStore } from '@/store/modules/toastStore'

export default defineComponent({
  name: 'CreateSupplierInvoiceView',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const toastStore = useToastStore()

    const loading = ref(true)
    const isSubmitting = ref(false)
    const supplier = ref(null)
    const availableOrders = ref([])
    const selectedOrderIds = ref([])
    const ordersData = ref({})

    const form = ref({
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      notes: ''
    })

    const errors = ref({})

    const canSubmit = computed(() => {
      return form.value.invoice_date && selectedOrderIds.value.length > 0
    })

    const totalAmount = computed(() => {
      return selectedOrderIds.value.reduce((sum, orderId) => {
        return sum + (Number(ordersData.value[orderId]?.amount) || 0)
      }, 0)
    })

    const totalDiscount = computed(() => {
      return selectedOrderIds.value.reduce((sum, orderId) => {
        return sum + (Number(ordersData.value[orderId]?.discount) || 0)
      }, 0)
    })

    const totalTax = computed(() => {
      return selectedOrderIds.value.reduce((sum, orderId) => {
        return sum + (Number(ordersData.value[orderId]?.tax_amount) || 0)
      }, 0)
    })

    const grandTotal = computed(() => {
      return totalAmount.value - totalDiscount.value + totalTax.value
    })

    const loadSupplierAndOrders = async () => {
      const supplierId = route.params.supplierId
      if (!supplierId) {
        toastStore.addToast({
          type: 'error',
          message: 'ID fournisseur manquant',
          duration: 3000
        })
        router.push({ name: 'suppliers' })
        return
      }

      try {
        loading.value = true

        const supplierRes = await supplierService.getSupplier(supplierId)
        supplier.value = supplierRes.data?.data || supplierRes.data || {}

        const ordersRes = await purchaseOrderService.getPurchaseOrders({
          supplier_id: supplierId,
          status: 'active',
          with_out_invoice: false
        })

        availableOrders.value = Array.isArray(ordersRes.data)
          ? ordersRes.data
          : (ordersRes.data.data || ordersRes.data.items || [])

        availableOrders.value.forEach(order => {
          ordersData.value[order.id] = {
            purchase_order_id: order.id,
            items_count: order.items_count,
            amount: order.total_amount,
            status: 'included',
            notes: ''
          }
        })
      } catch (e) {
        console.error('Error loading data:', e)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du chargement des données',
          duration: 3000
        })
      } finally {
        loading.value = false
      }
    }

    const calculateOrderTotal = (order) => {
      return order.total_amount;
    }

    const calculateOrderNetTotal = (orderId) => {
      const data = ordersData.value[orderId]
      if (!data) return 0
      return (Number(data.amount) || 0) - (Number(data.discount) || 0) + (Number(data.tax_amount) || 0)
    }

    const isOrderSelected = (orderId) => {
      return selectedOrderIds.value.includes(orderId)
    }

    const toggleOrder = (order) => {
      const index = selectedOrderIds.value.indexOf(order.id)
      if (index > -1) {
        selectedOrderIds.value.splice(index, 1)
      } else {
        selectedOrderIds.value.push(order.id)
      }
    }

    const getOrderData = (orderId) => {
      return ordersData.value[orderId] || {}
    }

    const handleSubmit = async () => {
      errors.value = {}

      if (!form.value.invoice_date) {
        errors.value.invoice_date = 'La date de facture est requise'
        return
      }

      if (selectedOrderIds.value.length === 0) {
        toastStore.addToast({
          type: 'error',
          message: 'Veuillez sélectionner au moins une commande',
          duration: 3000
        })
        return
      }

      try {
        isSubmitting.value = true

        const payload = {
          supplier_id: supplier.value.id,
          invoice_date: form.value.invoice_date,
          due_date: form.value.due_date || null,
          notes: form.value.notes || '',
          purchase_orders: selectedOrderIds.value.map(orderId => ({
            purchase_order_id: ordersData.value[orderId].purchase_order_id,
            amount: Number(ordersData.value[orderId].amount) || 0,
            discount: Number(ordersData.value[orderId].discount) || 0,
            tax_amount: Number(ordersData.value[orderId].tax_amount) || 0,
            status: ordersData.value[orderId].status || 'included',
            notes: ordersData.value[orderId].notes || ''
          }))
        }

        const res = await supplierInvoiceService.createSupplierInvoice(payload)
        const invoiceId = res.data?.data?.id || res.data?.id

        toastStore.addToast({
          type: 'success',
          message: 'Facture créée avec succès',
          duration: 3000
        })

        router.push({ name: 'supplier-invoice-details', params: { id: invoiceId } })
      } catch (e) {
        console.error('Error creating invoice:', e)
        const errorMessage = e.response?.data?.message || 'Erreur lors de la création de la facture'
        toastStore.addToast({
          type: 'error',
          message: errorMessage,
          duration: 3000
        })

        if (e.response?.data?.errors) {
          errors.value = e.response.data.errors
        }
      } finally {
        isSubmitting.value = false
      }
    }

    const handleCancel = () => {
      if (selectedOrderIds.value.length > 0 || form.value.notes) {
        if (confirm('Êtes-vous sûr de vouloir annuler ? Les modifications seront perdues.')) {
          router.push({ name: 'supplier-detail', params: { id: supplier.value.id } })
        }
      } else {
        router.push({ name: 'supplier-detail', params: { id: supplier.value.id } })
      }
    }

    const formatCurrency = (amount) => {
      return supplierInvoiceService.formatCurrency(amount)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      return supplierInvoiceService.formatDate(dateString)
    }

    onMounted(loadSupplierAndOrders)

    return {
      loading,
      isSubmitting,
      supplier,
      availableOrders,
      selectedOrderIds,
      ordersData,
      form,
      errors,
      canSubmit,
      totalAmount,
      totalDiscount,
      totalTax,
      grandTotal,
      calculateOrderTotal,
      calculateOrderNetTotal,
      isOrderSelected,
      toggleOrder,
      getOrderData,
      handleSubmit,
      handleCancel,
      formatCurrency,
      formatDate
    }
  }
})
</script>
